import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Chip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { useGarage } from '../../contexts/GarageContext';
import { apiService } from '../../services/api';
import AppointmentCard from './AppointmentCard';
import AppointmentSummaryModal from '../garage-admin/AppointmentSummaryModal';
import NewAppointmentModal from '../garage-admin/NewAppointmentModal';
import CategoryLegend from './CategoryLegend';
import ServicesList from './ServicesList';
import { CATEGORY_CONFIG } from '../../utils/categoryConstants';

const MonthView = ({ 
  currentDate, 
  selectedDate, 
  appointments, 
  onDateSelect, 
  onDateChange,
  getCategoryCounts,
  availableProducts,
  availableAddons,
  isWithinBusinessHours,
  isBusinessDay,
  onRefreshCalendar
}) => {
  const theme = useTheme();
  const { isDark } = useAppTheme();
  const { selectedGarage } = useGarage();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Local state for appointments list modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSelectedDate, setModalSelectedDate] = useState(null);
  
  // Local state for appointment details modal
  const [appointmentDetailsOpen, setAppointmentDetailsOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Local state for new appointment modal
  const [newAppointmentOpen, setNewAppointmentOpen] = useState(false);

  // Get calendar grid data
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // First day of the calendar grid (might be from previous month)
    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    startDate.setDate(firstDay.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1)); // Monday = 0
    
    // Generate 6 weeks of days (42 days)
    const days = [];
    const currentDay = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return days;
  };

  // Get appointments for a specific date
  const getAppointmentsForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return appointments.filter(apt => apt.date === dateStr);
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is selected
  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  // Check if date is in current month
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Handle date click
  const handleDateClick = (date) => {
    onDateSelect(date);
    setModalSelectedDate(date);
    setModalOpen(true);
  };

  // Handle appointments list modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setModalSelectedDate(null);
  };

  // Handle appointment card click - open appointment details
  const handleAppointmentClick = (appointment, event) => {
    event?.stopPropagation();
    // Pass the original booking data, not the transformed appointment
    setSelectedAppointment(appointment.originalBooking);
    setAppointmentDetailsOpen(true);
  };

  // Handle empty slot click - open new appointment modal  
  const handleSlotClick = (date, time, event) => {
    event?.stopPropagation();
    
    // Enhanced future date/time validation
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    // Don't allow past dates
    if (selectedDate < today) {
      return; // Don't open modal for past dates
    }

    // For today, check if there's still time left for appointments
    // (similar logic to other views but more lenient since no specific time in month view)
    if (selectedDate.getTime() === today.getTime()) {
      // If it's today, we'll allow it but the NewAppointmentModal will handle specific time validation
      // This gives users the option to book for later today if business hours allow
    }

    // Check if date is a business day
    if (isBusinessDay && !isBusinessDay(date)) {
      return; // Don't open modal for closed days
    }

    // Additional validation: check if the date has any available business hours
    // This ensures we don't open modal for days that are completely closed
    if (isWithinBusinessHours && !isWithinBusinessHours(date)) {
      return; // Don't open modal if no business hours defined for this day
    }
    
    // Set selected date in calendar and store for prefilling
    onDateSelect(date);
    setModalSelectedDate(date);
    setNewAppointmentOpen(true);
  };

  // Handle appointment modal close
  const handleAppointmentDetailsClose = () => {
    setAppointmentDetailsOpen(false);
    setSelectedAppointment(null);
  };

  // Handle new appointment modal close
  const handleNewAppointmentClose = () => {
    setNewAppointmentOpen(false);
    setModalSelectedDate(null);
  };

  // Handle appointment creation success
  const handleAppointmentSuccess = () => {
    handleNewAppointmentClose();
    // Refresh calendar to show new appointment
    if (onRefreshCalendar) {
      onRefreshCalendar();
    }
  };

  // Handle appointment data change
  const handleAppointmentDataChange = () => {
    // Refresh calendar to show updated appointment
    if (onRefreshCalendar) {
      onRefreshCalendar();
    }
  };

  const calendarDays = getCalendarDays();
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Get selected date appointments
  const selectedDateAppointments = selectedDate ? getAppointmentsForDate(selectedDate) : [];

  // Get modal appointments
  const modalSelectedDateAppointments = modalSelectedDate ? getAppointmentsForDate(modalSelectedDate) : [];

  return (
    <>
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        p: { xs: 1, sm: 1.5, md: 2 }
      }}>
        {/* Category Legend */}
        <CategoryLegend compact={isMobile} availableProducts={availableProducts} />

        {/* Calendar Grid - Full Width */}
        <Card sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          {/* Single scroll container for both headers and grid */}
          <Box 
            sx={{
              flex: 1,
              overflowX: { xs: 'auto', sm: 'hidden' },
              overflowY: 'hidden',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none'
            }}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: { xs: 'max-content', sm: 'auto' },
              height: '100%'
            }}>
              {/* Week day headers */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { 
                  xs: 'repeat(7, 120px)', 
                  sm: 'repeat(7, 1fr)' 
                },
                gap: { xs: 1, sm: 0 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: isDark ? 'grey.800' : 'grey.50',
                pb: { xs: 1, sm: 0 }
              }}>
                {weekDays.map((day) => (
                  <Box
                    key={day}
                    sx={{
                      p: { xs: 1, sm: 1.5, md: 2 },
                      textAlign: 'center',
                      borderRight: { xs: 'none', sm: '1px solid' },
                      borderColor: 'divider',
                      bgcolor: { xs: isDark ? 'grey.700' : 'grey.100', sm: 'transparent' },
                      borderRadius: { xs: 1, sm: 0 },
                      '&:last-child': {
                        borderRight: 'none'
                      }
                    }}
                  >
                    <Typography 
                      variant="subtitle2" 
                      fontWeight="bold"
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      {day}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Calendar grid */}
              <Box 
                sx={{
                  flex: 1,
                  display: 'grid',
                  gridTemplateColumns: { 
                    xs: 'repeat(7, 120px)', 
                    sm: 'repeat(7, 1fr)' 
                  },
                  gridTemplateRows: { xs: 'repeat(6, 100px)', sm: 'repeat(6, 1fr)' },
                  gap: { xs: 1, sm: 0 },
                  pt: { xs: 1, sm: 0 }
                }}
              >
                {calendarDays.map((date, index) => {
              const dayAppointments = getAppointmentsForDate(date);
              const isCurrentMonthDate = isCurrentMonth(date);
              const isTodayDate = isToday(date);
              const isSelectedDate = isSelected(date);
              
              // Enhanced availability checks
              const now = new Date();
              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
              const isPastDate = dateOnly < today;
              const isBusinessDayAvailable = !isBusinessDay || isBusinessDay(date);
              const hasBusinessHours = !isWithinBusinessHours || isWithinBusinessHours(date);
              const isDateAvailable = !isPastDate && isBusinessDayAvailable && hasBusinessHours;
              
              return (
                <Box
                  key={date.toISOString()}
                  onClick={(e) => {
                    // Only handle click if date is available
                    if (isDateAvailable) {
                      handleSlotClick(date, '09:00', e);
                    }
                  }}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderTop: index < 7 ? 'none' : '1px solid',
                    borderLeft: index % 7 === 0 ? 'none' : '1px solid',
                    p: 1,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: 100, sm: 'calc((100vh - 320px) / 6)', md: 'calc((100vh - 340px) / 6)' },
                    cursor: isDateAvailable ? 'pointer' : 'not-allowed',
                    opacity: isDateAvailable ? 1 : 0.9,
                    bgcolor: isSelectedDate 
                      ? 'primary.50' 
                      : isTodayDate
                        ? (isDark ? 'rgba(255, 143, 0, 0.06)' : 'rgba(255, 179, 0, 0.04)')
                        : !isCurrentMonthDate 
                          ? (isDark ? 'grey.900' : 'grey.100')
                          : !isDateAvailable
                            ? (isDark ? 'action.hover' : 'action.hover')
                            : 'background.paper',
                    // Professional today cell styling with DayView-style background
                    ...(isTodayDate && {
                      background: isDark 
                        ? `linear-gradient(135deg, rgba(255, 193, 7, 0.08) 0%, rgba(255, 160, 0, 0.06) 100%)`
                        : `linear-gradient(135deg, rgba(255, 193, 7, 0.06) 0%, rgba(255, 160, 0, 0.04) 100%)`,
                      borderLeft: `4px solid ${isDark ? '#ffb300' : '#ff8f00'}`,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: isDark
                          ? `linear-gradient(45deg, transparent 49%, rgba(255, 193, 7, 0.03) 50%, transparent 51%)`
                          : `linear-gradient(45deg, transparent 49%, rgba(255, 152, 0, 0.025) 50%, transparent 51%)`,
                        pointerEvents: 'none'
                      }
                    }),
                    '&:hover': {
                      bgcolor: isDateAvailable ? (
                        isSelectedDate 
                          ? 'primary.100' 
                          : 'action.hover'
                      ) : undefined
                    },
                    transition: 'background-color 0.2s ease',
                    // Add visual indicator for unavailable dates
                    ...(!isDateAvailable && {
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: isDark 
                          ? 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.02) 4px, rgba(255,255,255,0.02) 8px)'
                          : 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.02) 4px, rgba(0,0,0,0.02) 8px)',
                        pointerEvents: 'none'
                      }
                    })
                  }}
                >
                  {/* Date number */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1
                  }}>
                    <Typography
                      variant="body2"
                      fontWeight={isTodayDate ? 'bold' : 'normal'}
                      sx={{
                        fontSize: isTodayDate ? '1rem' : '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: isTodayDate ? { xs: 24, sm: 28, md: 32 } : 'auto',
                        height: isTodayDate ? { xs: 24, sm: 28, md: 32 } : 'auto',
                        borderRadius: '50%',
                        // Professional amber styling for today highlighting
                        bgcolor: isTodayDate 
                          ? (isDark ? '#ff8f00' : '#ffb300')
                          : 'transparent',
                        color: isTodayDate 
                          ? '#ffffff'
                          : (!isCurrentMonthDate 
                            ? 'text.disabled' 
                            : !isDateAvailable
                              ? 'text.secondary'
                              : 'text.primary'),
                        // Professional shadow and elevation for today
                        ...(isTodayDate && {
                          boxShadow: isDark 
                            ? '0 3px 8px rgba(255, 143, 0, 0.4), 0 1px 3px rgba(255, 143, 0, 0.3)'
                            : '0 3px 8px rgba(255, 179, 0, 0.35), 0 1px 3px rgba(255, 179, 0, 0.25)',
                          transform: 'scale(1.05)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          position: 'relative',
                          zIndex: 2
                        })
                      }}
                    >
                      {date.getDate()}
                    </Typography>
                    
                    {/* Category count badges */}
                    {getCategoryCounts && dayAppointments.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                        {(() => {
                          const counts = getCategoryCounts(dayAppointments);
                          
                          return Object.entries(counts)
                            .filter(([_, count]) => count > 0)
                            .map(([category, count]) => (
                              <Avatar
                                key={category}
                                sx={{
                                  width: 16,
                                  height: 16,
                                  fontSize: '0.5rem',
                                  fontWeight: 'bold',
                                  bgcolor: CATEGORY_CONFIG[category].color,
                                  color: 'white'
                                }}
                              >
                                {CATEGORY_CONFIG[category].letter}{count}
                              </Avatar>
                            ));
                        })()}
                      </Box>
                    )}
                  </Box>

                  {/* Appointments preview */}
                  <Box sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 0.5,
                    overflow: 'hidden'
                  }}>
                    {dayAppointments.slice(0, isMobile ? 1 : 2).map((appointment) => (
                      <AppointmentCard
                        key={appointment.id}
                        appointment={appointment}
                        variant="compact"
                        showTime={true}
                        showOnlyTimeAndVehicle={isMobile} // Show only time and vehicle on mobile
                        onClick={(e) => handleAppointmentClick(appointment, e)}
                      />
                    ))}
                    
                    {/* More indicator - only show if more than 1 appointment on mobile */}
                    {((isMobile && dayAppointments.length > 1) || 
                      (!isMobile && dayAppointments.length > 2)) && (
                      <Typography
                        variant="caption"
                        color="primary.main"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDateClick(date);
                        }}
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          mt: 0.5,
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.dark',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        +{dayAppointments.length - (isMobile ? 1 : 2)} more
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
                })}
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Appointments Modal Dialog */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            minHeight: '60vh',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {modalSelectedDate?.toLocaleDateString('en-GB', { 
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {modalSelectedDateAppointments.length} appointment{modalSelectedDateAppointments.length !== 1 ? 's' : ''}
              </Typography>
              
              {/* Category-wise counts */}
              {getCategoryCounts && (() => {
                const counts = getCategoryCounts(modalSelectedDateAppointments);
                
                return Object.entries(counts)
                  .filter(([_, count]) => count > 0)
                  .map(([category, count]) => (
                    <Avatar
                      key={category}
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        bgcolor: CATEGORY_CONFIG[category].color,
                        color: 'white'
                      }}
                    >
                      {CATEGORY_CONFIG[category].letter}{count}
                    </Avatar>
                  ));
              })()}
            </Box>
          </Box>
          <IconButton
            onClick={handleModalClose}
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {modalSelectedDateAppointments.length > 0 ? (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column'
            }}>
              {modalSelectedDateAppointments
                .sort((a, b) => a.startTime.localeCompare(b.startTime))
                .map((appointment, index) => (
                <Box key={appointment.id}>
                  {/* Time-based appointment item */}
                  <Box sx={{
                    py: 2,
                    borderBottom: index < modalSelectedDateAppointments.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}>
                    {/* Time at top-left */}
                    <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ 
                      fontSize: '0.75rem',
                      mb: 1,
                      display: 'block'
                    }}>
                      {appointment.startTime ? (() => {
                        const [hours, minutes] = appointment.startTime.split(':');
                        const hour12 = parseInt(hours) > 12 ? parseInt(hours) - 12 : parseInt(hours) === 0 ? 12 : parseInt(hours);
                        const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                        return `${hour12}:${minutes} ${ampm}`;
                      })() : 'No time'}
                    </Typography>

                    {/* Appointment Details */}
                    <Box>
                      {/* Vehicle Registration */}
                      <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                        {appointment.vehicleReg || 'Unknown Vehicle'}
                      </Typography>
                      
                      {/* Customer Name */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {appointment.customerName || 'Unknown Customer'}
                      </Typography>
                      
                      {/* Services */}
                      {appointment.services && appointment.services.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <ServicesList 
                            services={appointment.services} 
                            variant="modal"
                          />
                        </Box>
                      )}
                      
                      {/* Status */}
                      <Chip
                        label={appointment.status || 'Scheduled'}
                        size="small"
                        color={
                          appointment.status === 'completed' ? 'success' :
                          appointment.status === 'ongoing' ? 'warning' :
                          appointment.status === 'cancelled' ? 'error' : 'primary'
                        }
                        sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 6,
              color: 'text.secondary'
            }}>
              <Typography variant="body1">
                No appointments scheduled for this date
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Appointment Details Modal */}
      <AppointmentSummaryModal
        open={appointmentDetailsOpen}
        onClose={handleAppointmentDetailsClose}
        appointment={selectedAppointment}
        onDataChange={handleAppointmentDataChange}
        availableProducts={availableProducts}
        availableAddons={availableAddons}
      />

      {/* New Appointment Modal */}
      <NewAppointmentModal
        open={newAppointmentOpen}
        onClose={handleNewAppointmentClose}
        garageId={selectedGarage?.id}
        onSuccess={handleAppointmentSuccess}
        onError={(error) => console.error('Appointment creation error:', error)}
        availableProducts={availableProducts}
        availableAddons={availableAddons}
        prefilledDate={modalSelectedDate}
      />
    </>
  );
};

export default MonthView;